import { useState, useEffect, useRef } from 'react';
import { Loader2, AlertCircle, CheckCircle2, Filter, List, Sparkles, MessageCircle, X, Search } from 'lucide-react';
import {
  insertFormattedClause,
  isWordApiAvailable,
  getDocumentText,
  getSelectedText,
  replaceSelectedText,
} from '../utils/wordApi';
import { resolveContractContext } from '../utils/contractResolver';

// API base URL - can be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

interface Clause {
  id: string;
  title: string;
  content: string;
  category: string;
  lastUpdated: string;
  clauseType?: string;
  contractTypes: Array<{ id: string; name: string }>;
  fallbackClauses?: Array<{
    id: string;
    title: string;
    content: string;
    clauseType?: string;
  }>;
  tenantId?: string;
}

interface ClauseSuggestion {
  id: string;
  title: string;
  confidence: number;
  reason: string;
  preview: string;
  content: string;
  category: string;
}

interface CurrentContract {
  contractId: string;
  templateId: string | null;
  contractNumber: string | null;
  contractTitle: string | null;
  tenantId: string | null;
  companyId: string | null;
  templateTypeName?: string | null;
  templateTypeId?: string | null;
  lastUpdated: string;
}



interface ClauseWithFallbacks extends ClauseSuggestion {
  fallbackClauses?: Array<{
    id: string;
    title: string;
    content: string;
    clauseType?: string;
  }>;
}

export function ClauseSuggestion() {
  const [allClauses, setAllClauses] = useState<ClauseWithFallbacks[]>([]);
  const [filteredClauses, setFilteredClauses] = useState<ClauseWithFallbacks[]>([]);
  const [clauseDataMap, setClauseDataMap] = useState<Map<string, Clause>>(new Map());
  const [currentContract, setCurrentContract] = useState<CurrentContract | null>(null);
  const [templateTypeName, setTemplateTypeName] = useState<string>('');
  // Default to "All Clauses". If/when we resolve a contract type, an
  // effect below switches to the type-filtered tab.
  const [showAll, setShowAll] = useState<boolean>(true);
  const userHasPickedTab = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [insertingClauseId, setInsertingClauseId] = useState<string | null>(null);
  const [insertStatus, setInsertStatus] = useState<{ id: string; success: boolean; message: string } | null>(null);
  const [expandedClauses, setExpandedClauses] = useState<Set<string>>(new Set());
  const [missingClauseFilter, setMissingClauseFilter] = useState<{ type: string; clauseIds: string[] } | null>(null);
  const [renamedClauseFilter, setRenamedClauseFilter] = useState<string[] | null>(null);
  const [selectedClauseId, setSelectedClauseId] = useState<string | null>(null);
  const [clauseSuggestions, setClauseSuggestions] = useState<{
    fallbackClauses: any[];
    aiSuggestions: any[];
    comments: any[];
  } | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [selectedClauseText, setSelectedClauseText] = useState<string>('');
  const [selectionSuggestionsOpen, setSelectionSuggestionsOpen] = useState(false);
  const [selectionSuggestions, setSelectionSuggestions] = useState<{
    matchedClause: any | null;
    confidence?: number;
    fallbackClauses: any[];
    aiSuggestions: any[];
  } | null>(null);
  const [loadingSelectionSuggestions, setLoadingSelectionSuggestions] = useState(false);
  const [replacingSelection, setReplacingSelection] = useState(false);


  // Check for missing clause filter from dashboard
  useEffect(() => {
    const missingClausesData = localStorage.getItem('ezylex-missing-clauses');
    if (missingClausesData) {
      try {
        const filter = JSON.parse(missingClausesData);
        setMissingClauseFilter(filter);
        // Clear it after reading
        localStorage.removeItem('ezylex-missing-clauses');
      } catch (err) {
        console.error('Error parsing missing clauses filter:', err);
      }
    }
    
    // Check for renamed clauses filter
    const renamedClausesData = localStorage.getItem('ezylex-renamed-clauses');
    if (renamedClausesData) {
      try {
        const filter = JSON.parse(renamedClausesData);
        setRenamedClauseFilter(filter.clauseIds || []);
        // Clear it after reading
        localStorage.removeItem('ezylex-renamed-clauses');
      } catch (err) {
        console.error('Error parsing renamed clauses filter:', err);
      }
    }
    
    // Check for AI analysis of selected text
    const selectedText = sessionStorage.getItem('ezylex-ai-selected-text') || localStorage.getItem('ezylex-ai-selected-text');
    const shouldAnalyze = sessionStorage.getItem('ezylex-ai-analyze-selection') || localStorage.getItem('ezylex-ai-analyze-selection');
    
    if (selectedText && shouldAnalyze) {
      // Store selected text for AI analysis
      sessionStorage.setItem('ezylex-selected-text-for-analysis', selectedText);
      sessionStorage.removeItem('ezylex-ai-selected-text');
      sessionStorage.removeItem('ezylex-ai-analyze-selection');
      localStorage.removeItem('ezylex-ai-selected-text');
      localStorage.removeItem('ezylex-ai-analyze-selection');
      
      // Show a banner that AI analysis is available for selected text
      console.log('[ClauseSuggestion] Selected text for AI analysis:', selectedText.substring(0, 100));
    }
  }, []);

  const handleUseSelectedClause = async () => {
    if (!isWordApiAvailable()) {
      setError('Word API is not available. Please ensure you are running this add-in in Microsoft Word.');
      return;
    }

    if (!currentContract?.tenantId) {
      setError('Unable to determine tenant for this document.');
      return;
    }

    setError(null);
    setLoadingSelectionSuggestions(true);
    try {
      const selText = await getSelectedText();
      const trimmed = selText.trim();
      if (!trimmed) {
        setError('Select a clause in Word first, then click "Use Selected Clause".');
        return;
      }

      setSelectedClauseText(trimmed);
      const docText = await getDocumentText().catch(() => '');

      const qs = new URLSearchParams({ tenantId: currentContract.tenantId });
      if (currentContract.templateTypeId) {
        qs.set('contractTypeId', currentContract.templateTypeId);
      }
      if (currentContract.contractId) {
        qs.set('contractId', currentContract.contractId);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/addin/selected-clause-suggestions?${qs.toString()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            'x-addin-token': API_TOKEN,
          },
          body: JSON.stringify({
            tenantId: currentContract.tenantId,
            contractTypeId: currentContract.templateTypeId,
            contractId: currentContract.contractId,
            selectedText: trimmed,
            documentText: docText.substring(0, 8000),
          }),
        }
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to fetch selected clause suggestions');
      }

      setSelectionSuggestions({
        matchedClause: data?.matchedClause || null,
        confidence: data?.matchedClause?.confidence,
        fallbackClauses: Array.isArray(data?.fallbackClauses) ? data.fallbackClauses : [],
        aiSuggestions: Array.isArray(data?.aiSuggestions) ? data.aiSuggestions : [],
      });
      setSelectionSuggestionsOpen(true);
    } catch (err) {
      console.error('Error loading selection suggestions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load selection suggestions');
    } finally {
      setLoadingSelectionSuggestions(false);
    }
  };

  const replaceSelectionWith = async (text: string) => {
    if (!isWordApiAvailable()) {
      setError('Word API is not available. Please ensure you are running this add-in in Microsoft Word.');
      return;
    }
    setError(null);
    setReplacingSelection(true);
    try {
      await replaceSelectedText(text);
      setSelectionSuggestionsOpen(false);
    } catch (err) {
      console.error('Error replacing selection:', err);
      setError(err instanceof Error ? err.message : 'Failed to replace selection');
    } finally {
      setReplacingSelection(false);
    }
  };

  // Resolve current contract context (works with both WOPI and SharePoint/Auth modes)
  useEffect(() => {
    resolveContractContext().then((ctx) => {
      if (ctx?.contractId) {
        setCurrentContract(ctx as CurrentContract);
      } else {
        setCurrentContract(null);
      }
    }).catch((err) => console.error('Error loading current contract:', err));
  }, []);

  // Set template type name from current contract data
  useEffect(() => {
    if (currentContract?.templateTypeName) {
      setTemplateTypeName(currentContract.templateTypeName);
    }
  }, [currentContract?.templateTypeName]);

  // Once we know the contract type, switch to the type-filtered tab —
  // but only if the user hasn't manually picked a tab yet.
  useEffect(() => {
    if (templateTypeName && !userHasPickedTab.current) {
      setShowAll(false);
    }
  }, [templateTypeName]);

  // Fetch ALL clauses once (for the "All Clauses" tab)
  useEffect(() => {
    const fetchAllClauses = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/api/addin/clauses`, {
          headers: { 
            'ngrok-skip-browser-warning': 'true',
            'x-addin-token': API_TOKEN,
          },
        });
        const data = await response.json();
        const fetchedClauses: Clause[] = data.clauses || [];

        // Store full clause data for fallback access
        const dataMap = new Map<string, Clause>();
        fetchedClauses.forEach(clause => {
          dataMap.set(clause.id, clause);
        });
        setClauseDataMap(dataMap);

        // Transform clauses into suggestions format (exclude fallback clauses from main list)
        const suggestions: ClauseWithFallbacks[] = fetchedClauses.map((clause) => {
          const daysSinceUpdate = Math.floor(
            (Date.now() - new Date(clause.lastUpdated).getTime()) / (1000 * 60 * 60 * 24)
          );
          const confidence = Math.max(70, 100 - daysSinceUpdate * 2);

          const preview = clause.content.substring(0, 150) + (clause.content.length > 150 ? '...' : '');

          const reason = clause.contractTypes.length > 0
            ? `Recommended for ${clause.contractTypes.map(ct => ct.name).join(', ')} contracts`
            : `Commonly used ${clause.category} clause`;

          return {
            id: clause.id,
            title: clause.title,
            confidence: Math.min(95, confidence),
            reason,
            preview,
            content: clause.content,
            category: clause.category,
            fallbackClauses: clause.fallbackClauses || [],
          };
        });

        setAllClauses(suggestions);
      } catch (err) {
        console.error('Error fetching clauses:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch clauses from CLM');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllClauses();
  }, []);

  // Fetch template-filtered clauses when contract context is available
  useEffect(() => {
    const fetchFilteredClauses = async () => {
      if (!currentContract?.tenantId) {
        setFilteredClauses([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Build common params — server resolves contract_type via
        // contractTypeId (if provided) or contractId (falls back to
        // contracts.contract_type_id then template.contract_type_id).
        const baseParams: string[] = [
          `tenantId=${encodeURIComponent(currentContract.tenantId)}`,
        ];
        if (currentContract.templateTypeId) {
          baseParams.push(`contractTypeId=${encodeURIComponent(currentContract.templateTypeId)}`);
        }
        if (currentContract.contractId) {
          baseParams.push(`contractId=${encodeURIComponent(currentContract.contractId)}`);
        }

        // If we have missing clause filter, fetch specific clauses by type and IDs
        if (missingClauseFilter && missingClauseFilter.clauseIds.length > 0) {
          const clauseType = missingClauseFilter.type === 'mandatory' ? 'Mandatory' :
                           missingClauseFilter.type === 'optional' ? 'Optional' :
                           missingClauseFilter.type === 'conditional' ? 'Conditional' : null;

          const clauseIds = missingClauseFilter.clauseIds.join(',');
          const url = `${API_BASE_URL}/api/addin/clauses-by-type?${baseParams.join('&')}&clauseIds=${encodeURIComponent(clauseIds)}${clauseType ? `&clauseType=${encodeURIComponent(clauseType)}` : ''}`;
          
          const response = await fetch(url, {
            headers: { 
              'ngrok-skip-browser-warning': 'true',
              'x-addin-token': API_TOKEN,
            },
          });
          const data = await response.json();
          const fetchedClauses: Clause[] = data.clauses || [];
          
          // Store full clause data
          const dataMap = new Map<string, Clause>();
          fetchedClauses.forEach(clause => {
            dataMap.set(clause.id, clause);
          });
          setClauseDataMap(prev => new Map([...prev, ...dataMap]));

          const suggestions: ClauseWithFallbacks[] = fetchedClauses.map((clause) => {
            const daysSinceUpdate = Math.floor(
              (Date.now() - new Date(clause.lastUpdated).getTime()) / (1000 * 60 * 60 * 24)
            );
            const confidence = Math.max(70, 100 - daysSinceUpdate * 2);

            const preview = clause.content.substring(0, 150) + (clause.content.length > 150 ? '...' : '');

            const reason = clause.contractTypes.length > 0
              ? `Recommended for ${clause.contractTypes.map(ct => ct.name).join(', ')} contracts`
              : `Commonly used ${clause.category} clause`;

            return {
              id: clause.id,
              title: clause.title,
              confidence: Math.min(95, confidence),
              reason,
              preview,
              content: clause.content,
              category: clause.category,
              fallbackClauses: clause.fallbackClauses || [],
            };
          });

          setFilteredClauses(suggestions);
          setIsLoading(false);
          return;
        }

        // Normal fetch for all clauses — use the same param set so it
        // also handles direct-upload contracts (no template).
        const response = await fetch(
          `${API_BASE_URL}/api/addin/clauses?${baseParams.join('&')}`,
          {
            headers: {
              'ngrok-skip-browser-warning': 'true',
              'x-addin-token': API_TOKEN,
            },
          }
        );
        const data = await response.json();
        const fetchedClauses: Clause[] = data.clauses || [];
        // Server now tells us the contract type name it resolved to.
        // Use that for the filter chip instead of falling back to "MSA".
        if (data.contract_type_name) {
          setTemplateTypeName(String(data.contract_type_name));
        }

        // Store full clause data
        const dataMap = new Map<string, Clause>();
        fetchedClauses.forEach(clause => {
          dataMap.set(clause.id, clause);
        });
        setClauseDataMap(prev => new Map([...prev, ...dataMap]));

        const suggestions: ClauseWithFallbacks[] = fetchedClauses.map((clause) => {
          const daysSinceUpdate = Math.floor(
            (Date.now() - new Date(clause.lastUpdated).getTime()) / (1000 * 60 * 60 * 24)
          );
          const confidence = Math.max(70, 100 - daysSinceUpdate * 2);

          const preview = clause.content.substring(0, 150) + (clause.content.length > 150 ? '...' : '');

          const reason = clause.contractTypes.length > 0
            ? `Recommended for ${clause.contractTypes.map(ct => ct.name).join(', ')} contracts`
            : `Commonly used ${clause.category} clause`;

          return {
            id: clause.id,
            title: clause.title,
            confidence: Math.min(95, confidence),
            reason,
            preview,
            content: clause.content,
            category: clause.category,
            fallbackClauses: clause.fallbackClauses || [],
          };
        });

        setFilteredClauses(suggestions);
      } catch (err) {
        console.error('Error fetching filtered clauses:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch filtered clauses from CLM');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFilteredClauses();
  }, [currentContract?.templateTypeId, currentContract?.tenantId, currentContract?.contractId, missingClauseFilter]);

  const handleInsert = async (suggestion: ClauseSuggestion): Promise<void> => {
    if (!isWordApiAvailable()) {
      setInsertStatus({
        id: suggestion.id,
        success: false,
        message: 'Word API is not available. Please ensure you are running this add-in in Microsoft Word.'
      });
      setTimeout(() => setInsertStatus(null), 3000);
      return;
    }

    setInsertingClauseId(suggestion.id);
    setInsertStatus(null);

    try {
      await insertFormattedClause(suggestion.content);
      setInsertStatus({
        id: suggestion.id,
        success: true,
        message: `"${suggestion.title}" inserted successfully!`
      });
      
      setTimeout(() => {
        setInsertStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error inserting clause:', error);
      setInsertStatus({
        id: suggestion.id,
        success: false,
        message: `Failed to insert clause: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      
      setTimeout(() => {
        setInsertStatus(null);
      }, 5000);
    } finally {
      setInsertingClauseId(null);
    }
  };

  // Determine which clauses to show
  let clausesToShow: ClauseWithFallbacks[] = [];
  
  if (renamedClauseFilter && renamedClauseFilter.length > 0) {
    // Show only renamed clauses when navigated from dashboard
    const allClausesMap = new Map<string, ClauseWithFallbacks>();
    [...allClauses, ...filteredClauses].forEach(c => {
      if (!allClausesMap.has(c.id)) {
        allClausesMap.set(c.id, c);
      }
    });
    
    clausesToShow = renamedClauseFilter
      .map(id => allClausesMap.get(id))
      .filter((c): c is ClauseWithFallbacks => c !== undefined);
  } else if (missingClauseFilter && missingClauseFilter.clauseIds.length > 0) {
    // Show only missing clauses when navigated from dashboard
    // Combine all clauses from both sources (including those that might be filtered as fallback)
    const allClausesMap = new Map<string, ClauseWithFallbacks>();
    [...allClauses, ...filteredClauses].forEach(c => {
      if (!allClausesMap.has(c.id)) {
        allClausesMap.set(c.id, c);
      }
    });
    
    // Also check clauseDataMap which might have clauses not in the main lists
    clauseDataMap.forEach((clause, id) => {
      if (!allClausesMap.has(id)) {
        const daysSinceUpdate = Math.floor(
          (Date.now() - new Date(clause.lastUpdated).getTime()) / (1000 * 60 * 60 * 24)
        );
        const confidence = Math.max(70, 100 - daysSinceUpdate * 2);
        const preview = clause.content.substring(0, 150) + (clause.content.length > 150 ? '...' : '');
        const reason = clause.contractTypes.length > 0
          ? `Recommended for ${clause.contractTypes.map(ct => ct.name).join(', ')} contracts`
          : `Commonly used ${clause.category} clause`;
        
        allClausesMap.set(id, {
          id: clause.id,
          title: clause.title,
          confidence: Math.min(95, confidence),
          reason,
          preview,
          content: clause.content,
          category: clause.category,
          fallbackClauses: clause.fallbackClauses || [],
        });
      }
    });
    
    clausesToShow = missingClauseFilter.clauseIds
      .map(id => allClausesMap.get(id))
      .filter((c): c is ClauseWithFallbacks => c !== undefined);
    
    // When showing missing clauses, don't filter out fallback clauses
    // because they're explicitly missing and need to be shown
  } else {
    // Normal behavior: "All Clauses" = allClauses; contract type (e.g. NDA) = only filteredClauses (never fall back to all)
    clausesToShow = showAll ? allClauses : filteredClauses;
    
    // Filter out fallback clauses from main list (they should only appear under their parent clauses)
    // Get all fallback clause IDs from the clauses we're showing
    const fallbackClauseIds = new Set(
      clausesToShow.flatMap(c => c.fallbackClauses?.map(fb => fb.id) || [])
    );
    clausesToShow = clausesToShow.filter(c => !fallbackClauseIds.has(c.id));
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    clausesToShow = clausesToShow.filter(
      (clause) =>
        clause.title.toLowerCase().includes(query) ||
        clause.content.toLowerCase().includes(query) ||
        clause.category.toLowerCase().includes(query)
    );
  }

  return (
    <div className="h-full bg-gray-50 overflow-y-auto">
      <div className="p-3">
        {/* Compact Header */}
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-base font-bold text-gray-900">Clause Suggestions</h1>
          <button
            onClick={handleUseSelectedClause}
            disabled={loadingSelectionSuggestions}
            className="px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
            title="Select clause text in Word then click to get CLM + AI suggestions"
          >
            {loadingSelectionSuggestions ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            <span>Use Selected</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Error loading clauses</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Insert Status */}
        {insertStatus && (
          <div
            className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              insertStatus.success
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {insertStatus.success ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{insertStatus.message}</span>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search clauses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Filter Buttons - Only show if not filtering by missing clauses */}
        {!missingClauseFilter && (
          <div className="flex gap-2 mb-3">
            {/* Only show the type-filter tab if we actually know a type */}
            {templateTypeName && (
              <button
                type="button"
                onClick={() => {
                  userHasPickedTab.current = true;
                  setShowAll(false);
                }}
                className={`flex-1 px-2.5 py-2 rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs font-medium ${
                  !showAll
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-3.5 h-3.5" />
                <span className="truncate">{templateTypeName}</span>
                {!showAll && filteredClauses.length > 0 && (
                  <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] font-bold">
                    {filteredClauses.length}
                  </span>
                )}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                userHasPickedTab.current = true;
                setShowAll(true);
              }}
              className={`flex-1 px-2.5 py-2 rounded-lg transition-all flex items-center justify-center gap-1.5 text-xs font-medium ${
                showAll
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              <span>All Clauses</span>
              {showAll && allClauses.length > 0 && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] font-bold">
                  {allClauses.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Show filter info when filtering by missing clauses */}
        {missingClauseFilter && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Showing Missing {missingClauseFilter.type.charAt(0).toUpperCase() + missingClauseFilter.type.slice(1)} Clauses
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {clausesToShow.length} clause{clausesToShow.length !== 1 ? 's' : ''} found
                </p>
              </div>
              <button
                onClick={() => {
                  setMissingClauseFilter(null);
                  setRenamedClauseFilter(null);
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Show All
              </button>
            </div>
          </div>
        )}

        {/* Show filter info when filtering by renamed clauses */}
        {renamedClauseFilter && renamedClauseFilter.length > 0 && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Showing Renamed Clauses
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {clausesToShow.length} clause{clausesToShow.length !== 1 ? 's' : ''} found
                </p>
              </div>
              <button
                onClick={() => {
                  setMissingClauseFilter(null);
                  setRenamedClauseFilter(null);
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Show All
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && clausesToShow.length === 0 ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Loading clauses...</p>
          </div>
        ) : clausesToShow.length === 0 && !error ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              {showAll 
                ? 'No clauses found in your CLM library.'
                : templateTypeName
                ? `No clauses found for ${templateTypeName}.`
                : 'No contract type detected — switch to "All Clauses" to browse everything.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-0 border-t border-gray-200">
            {clausesToShow.map((suggestion) => {
              const isExpanded = expandedClauses.has(suggestion.id);
              const toggleExpand = () => {
                setExpandedClauses(prev => {
                  const next = new Set(prev);
                  if (next.has(suggestion.id)) {
                    next.delete(suggestion.id);
                  } else {
                    next.add(suggestion.id);
                  }
                  return next;
                });
              };
              
              return (
                <div
                  key={suggestion.id}
                  className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between px-3 py-2.5">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button
                        onClick={toggleExpand}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <span className="text-sm text-gray-900 font-medium truncate">{suggestion.title}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={async () => {
                          if (!currentContract?.tenantId) {
                            setError('Unable to determine tenant for this document.');
                            return;
                          }
                          setSelectedClauseId(suggestion.id);
                          setLoadingSuggestions(true);
                          try {
                            const documentText = await getDocumentText().catch(() => '');
                            const response = await fetch(
                              `${API_BASE_URL}/api/addin/clause-suggestions?clauseId=${suggestion.id}&tenantId=${encodeURIComponent(currentContract.tenantId)}&documentText=${encodeURIComponent(documentText.substring(0, 5000))}`,
                              {
                                headers: {
                                  'x-addin-token': API_TOKEN,
                                  'ngrok-skip-browser-warning': 'true',
                                },
                              }
                            );
                            if (response.ok) {
                              const data = await response.json();
                              // Get comments for this clause from analysis if available
                              const analysisData = localStorage.getItem(`ezylex-analysis-${currentContract?.tenantId}`);
                              const comments = analysisData ? JSON.parse(analysisData).comments?.filter((c: any) => c.clauseId === suggestion.id) || [] : [];
                              setClauseSuggestions({
                                fallbackClauses: data.fallbackClauses || [],
                                aiSuggestions: data.aiSuggestions || [],
                                comments: comments,
                              });
                            }
                          } catch (err) {
                            console.error('Error loading suggestions:', err);
                          } finally {
                            setLoadingSuggestions(false);
                          }
                        }}
                        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors bg-purple-600 text-white hover:bg-purple-700"
                        title="Get AI suggestions and comments"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleInsert(suggestion)}
                        disabled={insertingClauseId === suggestion.id}
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors font-bold text-xs ${
                          insertingClauseId === suggestion.id
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-black text-white hover:bg-gray-800'
                        }`}
                        title="Replace clause"
                      >
                        {insertingClauseId === suggestion.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'R'
                        )}
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-1 bg-gray-50">
                      <p className="text-xs text-gray-600 mb-2">
                        <span className="font-medium">Why: </span>
                        {suggestion.reason}
                      </p>
                      <p className="text-xs text-gray-700 bg-white p-2 rounded border border-gray-200 mb-3">
                        {suggestion.preview}
                      </p>
                      
                      {/* Fallback Clauses */}
                      {suggestion.fallbackClauses && suggestion.fallbackClauses.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-300">
                          <p className="text-[10px] font-semibold text-gray-700 mb-1.5">Fallback Clauses</p>
                          <div className="space-y-1.5">
                            {suggestion.fallbackClauses.map((fallback) => (
                              <div key={fallback.id} className="bg-purple-50 rounded border border-purple-200 p-1.5">
                                <div className="flex items-center justify-between mb-1">
                                  <h5 className="text-xs font-semibold text-purple-900">{fallback.title}</h5>
                                  <button
                                    onClick={() => handleInsert({
                                      id: fallback.id,
                                      title: fallback.title,
                                      content: fallback.content,
                                      confidence: 80,
                                      reason: 'Fallback clause',
                                      preview: fallback.content.substring(0, 150),
                                      category: 'Fallback',
                                    })}
                                    disabled={insertingClauseId === fallback.id}
                                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 font-bold text-[10px] shadow-sm"
                                  >
                                    {insertingClauseId === fallback.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      'R'
                                    )}
                                  </button>
                                </div>
                                <p className="text-[11px] text-purple-700 leading-relaxed">{fallback.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
            </div>
              );
            })}
        </div>
        )}

        {/* Clause Detail Modal with Comments and Suggestions */}
        {selectedClauseId && clauseSuggestions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-2 flex items-center justify-between">
                <h2 className="text-[10px] font-semibold text-gray-900 uppercase tracking-wide">
                  Suggestions
                </h2>
                <button
                  onClick={() => {
                    setSelectedClauseId(null);
                    setClauseSuggestions(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Comments Section */}
                {clauseSuggestions.comments.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Comments ({clauseSuggestions.comments.length})
                    </h3>
                    <div className="space-y-2">
                      {clauseSuggestions.comments.map((comment: any) => (
                        <div
                          key={comment.id}
                          className={`p-3 rounded-lg border ${
                            comment.isNotAccepted
                              ? 'bg-red-50 border-red-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-gray-700">{comment.author}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(comment.date).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-xs text-gray-800">{comment.content}</p>
                          {comment.isNotAccepted && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded">
                              Not Accepted
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fallback Clauses Section */}
                {clauseSuggestions.fallbackClauses.length > 0 && (
                  <div>
                    <h3 className="text-[9px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                      Fallback
                    </h3>
                    <div className="space-y-1.5">
                      {clauseSuggestions.fallbackClauses.map((fb: any) => (
                        <div
                          key={fb.id}
                          className="bg-blue-50 border border-blue-200 rounded-lg p-2"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <h4 className="text-sm font-semibold text-blue-900">{fb.title}</h4>
                            <button
                              onClick={() => handleInsert({
                                id: fb.id,
                                title: fb.title,
                                content: fb.content,
                                confidence: 85,
                                reason: 'Fallback clause',
                                preview: fb.content.substring(0, 150),
                                category: '',
                              })}
                              disabled={insertingClauseId === fb.id}
                              className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-bold text-xs shadow-sm"
                            >
                              {insertingClauseId === fb.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'R'}
                            </button>
                          </div>
                          <p className="text-xs text-blue-800 leading-relaxed">{fb.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Suggestions Section */}
                {clauseSuggestions.aiSuggestions.length > 0 && (
                  <div>
                    <h3 className="text-[9px] font-semibold text-gray-700 mb-1 flex items-center gap-1 uppercase tracking-wide">
                      <Sparkles className="w-3 h-3 text-purple-600" />
                      AI
                    </h3>
                    <div className="space-y-1.5">
                      {clauseSuggestions.aiSuggestions.map((ai: any, index: number) => (
                        <div
                          key={index}
                          className="bg-purple-50 border border-purple-200 rounded-lg p-2"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <h4 className="text-sm font-semibold text-purple-900">{ai.title}</h4>
                            <button
                              onClick={() => handleInsert({
                                id: `ai-${index}`,
                                title: ai.title,
                                content: ai.content,
                                confidence: 80,
                                reason: ai.reason || 'AI-generated alternative',
                                preview: ai.content.substring(0, 150),
                                category: '',
                              })}
                              disabled={insertingClauseId === `ai-${index}`}
                              className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 font-bold text-xs shadow-sm"
                            >
                              {insertingClauseId === `ai-${index}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'R'}
                            </button>
                          </div>
                          <p className="text-xs text-purple-800 leading-relaxed">{ai.content}</p>
                          {ai.reason && (
                            <p className="text-[10px] text-purple-700 italic mt-1">💡 {ai.reason}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {clauseSuggestions.comments.length === 0 && 
                 clauseSuggestions.fallbackClauses.length === 0 && 
                 clauseSuggestions.aiSuggestions.length === 0 && (
                  <div className="text-center py-8 text-sm text-gray-500">
                    No comments or suggestions available for this clause.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {loadingSuggestions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-sm text-gray-700">Loading suggestions...</span>
            </div>
          </div>
        )}

        {selectionSuggestionsOpen && selectionSuggestions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-2 flex items-center justify-between">
                <h2 className="text-[10px] font-semibold text-gray-900 uppercase tracking-wide">Suggestions</h2>
                <button
                  onClick={() => {
                    setSelectionSuggestionsOpen(false);
                    setSelectionSuggestions(null);
                    setSelectedClauseText('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-3 space-y-3">
                <div>
                  <div className="text-[10px] font-semibold text-gray-700 mb-1">Selected Text</div>
                  <div className="text-[11px] text-gray-800 bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap leading-relaxed">
                    {selectedClauseText}
                  </div>
                </div>

                {selectionSuggestions.matchedClause && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[9px] font-semibold text-blue-700 uppercase tracking-wide">Matched Clause</div>
                      <button
                        onClick={() => replaceSelectionWith(selectionSuggestions.matchedClause.content)}
                        disabled={replacingSelection}
                        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-bold text-xs shadow-sm"
                      >
                        R
                      </button>
                    </div>
                    <div className="text-sm text-blue-900 font-semibold mb-1.5">{selectionSuggestions.matchedClause.title}</div>
                    <div className="text-xs text-blue-800 whitespace-pre-wrap leading-relaxed">
                      {selectionSuggestions.matchedClause.content}
                    </div>
                    <div className="text-[10px] text-blue-700 mt-1">
                      Match: {Math.round(((selectionSuggestions.confidence || 0) * 100) as number)}%
                    </div>
                  </div>
                )}

                {selectionSuggestions.fallbackClauses.length > 0 && (
                  <div>
                    <div className="text-[9px] font-semibold text-gray-700 mb-1 uppercase tracking-wide">Fallback</div>
                    <div className="space-y-1.5">
                      {selectionSuggestions.fallbackClauses.map((fb: any) => (
                        <div key={fb.id} className="bg-purple-50 border border-purple-200 rounded-lg p-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="text-sm font-semibold text-purple-900">{fb.title}</div>
                            <button
                              onClick={() => replaceSelectionWith(fb.content)}
                              disabled={replacingSelection}
                              className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 font-bold text-xs shadow-sm"
                            >
                              R
                            </button>
                          </div>
                          <div className="text-xs text-purple-800 whitespace-pre-wrap leading-relaxed">{fb.content}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectionSuggestions.aiSuggestions.length > 0 && (
                  <div>
                    <div className="text-[9px] font-semibold text-gray-700 mb-1 flex items-center gap-1 uppercase tracking-wide">
                      <Sparkles className="w-3 h-3 text-purple-600" /> AI
                    </div>
                    <div className="space-y-1.5">
                      {selectionSuggestions.aiSuggestions.map((ai: any, idx: number) => (
                        <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="text-sm font-semibold text-green-900">{ai.title}</div>
                            <button
                              onClick={() => replaceSelectionWith(ai.content)}
                              disabled={replacingSelection}
                              className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 font-bold text-xs shadow-sm"
                            >
                              R
                            </button>
                          </div>
                          <div className="text-xs text-green-800 whitespace-pre-wrap leading-relaxed">{ai.content}</div>
                          {ai.reason ? (
                            <div className="text-[10px] text-green-700 italic mt-1">💡 {ai.reason}</div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!selectionSuggestions.matchedClause &&
                  selectionSuggestions.fallbackClauses.length === 0 &&
                  selectionSuggestions.aiSuggestions.length === 0 && (
                    <div className="text-center py-8 text-sm text-gray-500">
                      No suggestions available for this selection.
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
